import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import apiService from '../../services/ApiService';

interface AudienceCardProps {
  eyebrow: string;
  title: string;
  body: string;
  points: string[];
  ctaLabel: string;
  href: string;
  ctaId: string;
  imageSrc: string;
  imageAlt: string;
  featured?: boolean;
}

const AudienceCard: React.FC<AudienceCardProps> = ({
  eyebrow,
  title,
  body,
  points,
  ctaLabel,
  href,
  ctaId,
  imageSrc,
  imageAlt,
  featured = false,
}) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  return (
    <article
      className={`relative flex flex-col overflow-hidden rounded-2xl border border-white/[0.06] ${
        featured ? 'bg-gradient-to-b from-orange-500/[0.07] to-[#0c0c0c]' : 'bg-[#0c0c0c]'
      }`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={imageSrc}
          alt={imageAlt}
          className={`w-full h-full object-cover transition-transform duration-700 ease-out ${
            hovered ? 'scale-[1.04]' : 'scale-100'
          }`}
          loading="lazy"
          decoding="async"
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/45 to-transparent"
          aria-hidden
        />
        <p className="absolute bottom-4 left-5 kicker text-orange-300/90">{eyebrow}</p>
      </div>
      <div className="flex flex-col flex-1 px-5 py-6 sm:px-7 sm:py-8">
        <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-white mb-2">
          {title}
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed mb-5 max-w-[42ch]">{body}</p>
        <ul className="mb-8 text-sm text-gray-300 space-y-2.5 flex-1">
          {points.map((point) => (
            <li key={point} className="flex gap-3">
              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-orange-400" aria-hidden />
              <span>{point}</span>
            </li>
          ))}
        </ul>
        <div className="mt-auto">
          <Button
            type="button"
            variant={featured ? 'primary' : 'secondary'}
            onClick={() => {
              void apiService.trackEvent('home_cta_clicked', {
                metadata: { cta: ctaId, surface: 'audience' },
              });
              navigate(href);
            }}
          >
            {ctaLabel}
          </Button>
        </div>
      </div>
    </article>
  );
};

/** Parallel conversion paths for organizers and clubs. Distinct imagery per audience. */
export const HomeAudienceSplit: React.FC = () => (
  <section className="py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-12 sm:mb-16 max-w-2xl">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white text-balance">
          Builders, organizers, clubs
        </h2>
        <p className="text-gray-400 text-base mt-4 leading-relaxed max-w-[48ch]">
          Builders document the car. Organizers and clubs use the same profiles at the event and in
          the club.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
        <AudienceCard
          eyebrow="For organizers"
          title="Run the event on real build profiles"
          body="Registration, check-in, judging, and awards without rebuilding the entry sheet each time."
          points={[
            'Publish an event and open registration',
            'Assign judges and collect scores',
            'Publish awards onto build profiles',
          ]}
          ctaLabel="Create event"
          href="/organizer/events/new"
          ctaId="organizer_convert"
          imageSrc="/images/brand/home-organizer-checkin.jpg"
          imageAlt="Organizer with a tablet checking in a modified car at a cool daylight paddock"
          featured
        />
        <AudienceCard
          eyebrow="For clubs"
          title="Find your people"
          body="Club profiles, featured cars, and badges that appear where the vehicle already lives."
          points={[
            'Create or browse club pages',
            'Feature member builds',
            'Issue badges that show on the profile',
          ]}
          ctaLabel="Browse clubs"
          href="/clubs"
          ctaId="club_convert"
          imageSrc="/images/brand/home-club-gathering.jpg"
          imageAlt="Car club members gathered between modified cars at cool twilight"
        />
      </div>
    </div>
  </section>
);

export default HomeAudienceSplit;
